import { datadogLogs } from "@datadog/browser-logs";
import type { Site } from "@datadog/browser-rum";
import { datadogRum, DefaultPrivacyLevel } from "@datadog/browser-rum";

import ProcessEnvironment from "@/constants/ProcessEnvironment";
import { AppVersion } from "@/utils/appVersion.util";
import {
  isLocalEnvironment,
  isProductionEnvironment,
} from "@/utils/environment.util";

const {
  DatadogWebClientToken,
  DatadogWebRUMApplicationId,
  DeploymentEnvironment,
} = ProcessEnvironment;

const MaxExtensionScanDepth = 3;

const isExtensionUrl = (value: unknown): boolean =>
  typeof value === "string" &&
  (value.startsWith("chrome-extension://") ||
    value.startsWith("moz-extension://"));

// (from my-equip web) drop events whose URLs come from browser extensions (noise)
const hasExtensionUrl = ({
  depth = 0,
  value,
}: {
  depth?: number;
  value: unknown;
}): boolean => {
  // base case -> exit early to limit recursive depth
  if (depth > MaxExtensionScanDepth) return false;

  // if string, can check directly if an extension url
  if (typeof value === "string") return isExtensionUrl(value);

  // if array, recurse on each item on the array
  if (Array.isArray(value)) {
    return value.some((item) =>
      hasExtensionUrl({ depth: depth + 1, value: item }),
    );
  }

  // if object, recurse on each value in the object
  if (value && typeof value === "object") {
    return Object.values(value).some((item) =>
      hasExtensionUrl({ depth: depth + 1, value: item }),
    );
  }

  return false;
};

// (from my-equip web) mask PII in prod, full capture in lower envs for debugging
// eslint-disable-next-line @typescript-eslint/naming-convention -- PascalCase DefaultPrivacyLevel is the imported enum
const defaultPrivacyLevel = isProductionEnvironment()
  ? DefaultPrivacyLevel.MASK
  : DefaultPrivacyLevel.ALLOW;

const Service = "care";
// eslint-disable-next-line @typescript-eslint/naming-convention -- PascalCase Site is the imported type
const site: Site = "datadoghq.com";
// percentage of sessions captured for Session Replay
const SessionReplaySampleRate = 5;

const initDatadog = async (): Promise<void> => {
  try {
    /* eslint-disable @typescript-eslint/naming-convention -- Datadog browser SDK init keys are fixed by the SDK */
    datadogRum.init({
      allowedTracingUrls: [
        {
          match: /https:\/\/.*\.equip\.health.*/,
          propagatorTypes: ["datadog"],
        },
      ],
      applicationId: DatadogWebRUMApplicationId,
      // drop noisy events originating from browser extensions
      beforeSend: (event) => !hasExtensionUrl({ value: event }),
      clientToken: DatadogWebClientToken,
      defaultPrivacyLevel,
      // "test" when running locally, otherwise the deployment environment
      env: isLocalEnvironment() ? "test" : DeploymentEnvironment,
      service: Service,
      sessionReplaySampleRate: SessionReplaySampleRate,
      site,
      // sample tracing for network calls between app and backend (matches native's 80%)
      traceSampleRate: 80,
      trackLongTasks: true,
      trackResources: true,
      // expo-router drives nav, so views are reported manually via DdRum.startView in RootNavigator
      trackUserInteractions: true,
      trackViewsManually: true,
      version: AppVersion,
    });

    datadogLogs.init({
      beforeSend: (log) => !hasExtensionUrl({ value: log }),
      clientToken: DatadogWebClientToken,
      env: isLocalEnvironment() ? "test" : DeploymentEnvironment,
      forwardErrorsToLogs: true,
      service: Service,
      site,
      version: AppVersion,
    });
    /* eslint-enable @typescript-eslint/naming-convention -- Datadog browser SDK init keys are fixed by the SDK */
  } catch (error) {
    // eslint-disable-next-line no-console -- if DataDog, the app monitor fails, no alternative but console.error to report it
    console.error("Error initializing Datadog (web):", error);
  }
};

const DdLogs = {
  // eslint-disable-next-line max-params -- mirrors expo-datadog SDK signature
  info: async (
    message = "",
    context?: Record<string, unknown>,
  ): Promise<void> => {
    datadogLogs.logger.info(message, context);
  },
};

const DdRum = {
  // browser SDK takes only (name, context), so the native type/timestamp args are dropped
  // eslint-disable-next-line max-params -- mirrors expo-datadog SDK signature
  addAction: async (
    _type = "",
    name = "",
    context?: Record<string, unknown>,
    _timestampMs?: number,
  ): Promise<void> => {
    datadogRum.addAction(name, context);
  },
  // browser SDK takes (error, context), so source/stack are folded into the context
  // eslint-disable-next-line max-params -- mirrors expo-datadog SDK signature
  addError: async (
    message = "",
    source?: string,
    stack?: string,
    context?: Record<string, unknown>,
  ): Promise<void> => {
    datadogRum.addError(message, { ...context, errorSource: source, stack });
  },
  // default to key to capture full view path rather than pathname (just the base/leaf name)
  // eslint-disable-next-line max-params -- mirrors expo-datadog SDK signature
  startView: async (key = "", name?: string): Promise<void> => {
    datadogRum.startView({ name: key || name });
  },
};

const setDatadogUser = async (userId: string): Promise<void> => {
  datadogRum.setUser({ id: userId });
};

const setDatadogAttributes = async (
  attributes: Record<string, boolean | null | number | string | undefined>,
): Promise<void> => {
  datadogRum.setGlobalContext(attributes);
};

// mirror the enums exposed by native datadog
enum ErrorSource {
  CUSTOM = "custom",
  NETWORK = "network",
}
const RumActionType = { CUSTOM: "CUSTOM" };

export {
  DdLogs,
  DdRum,
  ErrorSource,
  hasExtensionUrl,
  initDatadog,
  RumActionType,
  setDatadogAttributes,
  setDatadogUser,
};
